/* earbit hero slideshow: crossfading full-bleed slides with arrows, dots,
   swipe, keyboard and a pause control.

   Autoplay follows WCAG 2.2.2: there is always a pause button, and rotation
   also holds while the pointer is over the hero, while keyboard focus is inside
   it, while the tab is hidden, and never starts for reduced-motion visitors.
   Inactive slides are `inert`, so their links are not in the tab order. */

class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.track = this.querySelector('[data-track]');
    this.pauseButton = this.querySelector('[data-pause]');
    this.index = 0;
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this.autoplay = this.dataset.autoplay === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.userPaused = false;
    this.hovered = false;
    this.focused = false;

    this.onResize = () => this.measureChrome();
    this.measureChrome();
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('load', this.onResize);

    if (this.slides.length < 2) return;

    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    this.querySelector('[data-prev]')?.addEventListener('click', this.onPrev);
    this.querySelector('[data-next]')?.addEventListener('click', this.onNext);

    this.onDot = (event) => {
      this.goTo(parseInt(event.currentTarget.dataset.index, 10), true);
    };
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));

    this.onPause = () => this.setUserPaused(!this.userPaused);
    this.pauseButton?.addEventListener('click', this.onPause);

    this.onKeydown = (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    };
    this.addEventListener('keydown', this.onKeydown);

    // Rotation holds while someone is looking at or working inside the hero.
    this.onPointerEnter = () => {
      this.hovered = true;
      this.sync();
    };
    this.onPointerLeave = () => {
      this.hovered = false;
      this.sync();
    };
    this.onFocusIn = () => {
      this.focused = true;
      this.sync();
    };
    this.onFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.focused = false;
      this.sync();
    };
    this.onVisibility = () => this.sync();
    // Hover holds only over the buttons and controls. The hero fills the screen,
    // so pausing on any hover would mean it almost never rotates on desktop;
    // this way it only waits when someone is about to click something.
    this.hoverTargets = Array.from(
      this.querySelectorAll(
        '.hero-slideshow__button, .hero-slideshow__pause, .hero-slideshow__dots, .hero-slideshow__arrow',
      ),
    );
    this.hoverTargets.forEach((target) => {
      target.addEventListener('pointerenter', this.onPointerEnter);
      target.addEventListener('pointerleave', this.onPointerLeave);
    });
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('visibilitychange', this.onVisibility);

    // Horizontal swipe. Vertical movement is left to the page scroll.
    this.onPointerDown = (event) => {
      if (event.pointerType === 'mouse') return;
      this.swipeStart = { x: event.clientX, y: event.clientY };
    };
    this.onPointerUp = (event) => {
      if (!this.swipeStart) return;
      const dx = event.clientX - this.swipeStart.x;
      const dy = event.clientY - this.swipeStart.y;
      this.swipeStart = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
    };
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', () => (this.swipeStart = null));

    // Theme editor: show the slide being edited and hold it there.
    this.onBlockSelect = (event) => {
      const i = this.slides.findIndex((slide) => slide.contains(event.target));
      if (i < 0) return;
      this.editorHold = true;
      this.goTo(i);
      this.sync();
    };
    this.onBlockDeselect = () => {
      this.editorHold = false;
      this.sync();
    };
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    if (this.reducedMotion && this.pauseButton) this.setUserPaused(true);
    this.sync();
  }

  disconnectedCallback() {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('load', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  measureChrome() {
    const chrome = Array.from(document.querySelectorAll('.shopify-section-group-header-group')).reduce(
      (total, node) => total + node.offsetHeight,
      0,
    );
    if (chrome > 0) this.style.setProperty('--earbit-chrome', `${chrome}px`);
  }

  get shouldRun() {
    return (
      this.autoplay &&
      !this.reducedMotion &&
      !this.userPaused &&
      !this.hovered &&
      !this.focused &&
      !this.editorHold &&
      !document.hidden
    );
  }

  sync() {
    if (this.shouldRun) this.start();
    else this.stop();
    // Announce slide changes only when a person is driving them.
    if (this.track) this.track.setAttribute('aria-live', this.shouldRun ? 'off' : 'polite');
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.goTo(this.index + 1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  step(delta) {
    this.goTo(this.index + delta, true);
  }

  goTo(index, userInitiated = false) {
    const count = this.slides.length;
    this.index = (index + count) % count;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
    });
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    // A manual change restarts the clock so the new slide gets its full time.
    if (userInitiated && this.timer) {
      this.stop();
      this.sync();
    }
  }

  setUserPaused(paused) {
    this.userPaused = paused;
    if (this.pauseButton) {
      this.pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.pauseButton.setAttribute(
        'aria-label',
        paused ? this.dataset.labelPlay || 'Play slideshow' : this.dataset.labelPause || 'Pause slideshow',
      );
    }
    this.sync();
  }
}

customElements.define('hero-slideshow', HeroSlideshow);
